import { canViewAssetThread, getAssetComputerAppUrl, getAssetPrimaryAction, getAssetThreadUrl, getHumanizedAssetDisplayName, } from '#pplx/frontend/apps/ask/asset-utils/assetHelpers.js';
import { isExternalUrlAsset } from '#pplx/frontend/apps/ask/asset-utils/externalUrlAsset.js';
import { getExternalUrlDomain } from '#pplx/frontend/apps/ask/canvas-shared/externalUrl.js';
import { linkArtifactOpenedEvent } from '#pplx/frontend/apps/ask/events/canvas/analytics.js';
import { useAssetAction } from '#pplx/frontend/apps/ask/hooks/library-hooks/useAssetAction.js';
import { useInstrumentation } from '#pplx/frontend/framework/instrumentation/useInstrumentation.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import { useRouter } from '#pplx/frontend/router/provider.js';
import { useCallback } from 'react';
function getArtifactMediaLightboxState(asset, options = {}) {
    const filename = options.displayName || getHumanizedAssetDisplayName(asset);
    const previewImageUrl = options.previewImageUrl ?? asset.preview_image_url;
    const threadUrl = canViewAssetThread(asset) ? getAssetThreadUrl(asset) : undefined;
    const isComputerTask = asset.source === 'asi';
    if (asset.asset_type === 'GENERATED_IMAGE' ||
        asset.asset_type === 'CHART' ||
        asset.asset_type === 'SCREENSHOT') {
        const url = previewImageUrl || asset.location;
        if (!url)
            return null;
        return {
            type: 'image',
            url,
            filename: filename || 'image.jpg',
            threadUrl,
            isComputerTask,
            assetType: asset.asset_type,
            assetUUID: asset.asset_id,
            ...(options.threadLinkTrack ? { threadLinkTrack: options.threadLinkTrack } : {}),
        };
    }
    if (asset.asset_type === 'GENERATED_VIDEO') {
        const url = asset.location;
        if (!url)
            return null;
        return {
            type: 'video',
            url,
            posterUrl: previewImageUrl || undefined,
            filename: filename || 'video.mp4',
            threadUrl,
            isComputerTask,
            assetType: asset.asset_type,
            assetUUID: asset.asset_id,
        };
    }
    return null;
}
export function useArtifactOpen({ onMediaClick, surface, }) {
    const executeAction = useAssetAction();
    const { device: { isWindowsApp }, } = useWebPlatform();
    const router = useRouter();
    const { track } = useInstrumentation();
    return useCallback((asset, options = {}) => {
        // Links leave the app instead of opening a canvas, so they get their own
        // event; host only, since a path can identify a document.
        const linkDomain = isExternalUrlAsset(asset)
            ? getExternalUrlDomain(asset.location)
            : undefined;
        if (linkDomain) {
            track(linkArtifactOpenedEvent({
                name: 'link artifact opened',
                data: {
                    surface,
                    domain: linkDomain,
                    assetUUID: asset.asset_id,
                    entryUUID: asset.entry_uuid ?? undefined,
                },
            }));
        }
        // Links have no canvas route for the desktop shell to push.
        if (isWindowsApp && !isExternalUrlAsset(asset)) {
            router.push(getAssetComputerAppUrl(asset), undefined, 'windows-artifacts-asset-open');
            return;
        }
        const mediaLightboxState = getArtifactMediaLightboxState(asset, options);
        if (mediaLightboxState) {
            onMediaClick(mediaLightboxState);
            return;
        }
        executeAction(options.nonMediaActionOverride ?? getAssetPrimaryAction(asset));
    }, [executeAction, isWindowsApp, onMediaClick, router, surface, track]);
}
//# sourceMappingURL=useArtifactOpen.js.map